import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion } from "framer-motion";
import { useSiteSettings } from "@/lib/SiteSettingsContext";
import { CTA } from "@/lib/ctas";

const SECTIONS = [
    { id: "services", label: "Services" },
    { id: "process", label: "Process" },
    { id: "work", label: "Work" },
    { id: "why", label: "Why us" },
];

export default function HomeHeader() {
    const [open, setOpen] = useState(false);
    const { settings } = useSiteSettings();
    const navigate = useNavigate();
    useEffect(() => setOpen(false), []);

    const onAnchor = (e, id) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        try { window.history.pushState(null, "", `#${id}`); } catch (_) {}
        setOpen(false);
    };

    return (
        <header className="h-nav" data-testid="home-header">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2.5" data-testid="home-logo">
                    <motion.span
                        className="w-9 h-9 rounded-full grid place-items-center text-white font-display font-bold text-lg"
                        style={{ background: "var(--grad-btn)", boxShadow: "0 0 18px -2px rgba(124,107,255,0.6)" }}
                        whileHover={{ rotate: 15 }}
                        transition={{ type: "spring", stiffness: 300, damping: 12 }}
                    >Ω</motion.span>
                    <span className="font-display font-bold text-[15px] tracking-tight" style={{ color: "var(--text)" }}>
                        {settings.company_name || "Omega IT"}
                    </span>
                </Link>
                <nav className="hidden lg:flex items-center gap-6">
                    {SECTIONS.map((s) => (
                        <a key={s.id} href={`#${s.id}`} onClick={(e) => onAnchor(e, s.id)} className="nav-link" data-testid={`home-nav-${s.id}`}>{s.label}</a>
                    ))}
                </nav>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => navigate(CTA.CONTACT.href)} className="btn-primary hidden lg:inline-flex" data-testid="home-header-cta">
                        {CTA.CONTACT.label} →
                    </button>
                    <button type="button" className="lg:hidden w-10 h-10 grid place-items-center rounded-md" style={{ color: "var(--text)" }} onClick={() => setOpen((v) => !v)} aria-label={open ? "Close menu" : "Open menu"} data-testid="home-mobile-toggle">
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="lg:hidden border-t" style={{ borderColor: "var(--line-soft)", background: "var(--ink)" }}>
                    <div className="px-5 sm:px-8 py-4 flex flex-col gap-1">
                        {SECTIONS.map((s) => (
                            <a key={s.id} href={`#${s.id}`} onClick={(e) => onAnchor(e, s.id)} className="px-3 py-3 text-sm font-semibold" style={{ color: "var(--text)" }}>{s.label}</a>
                        ))}
                        <button type="button" onClick={() => navigate(CTA.CONTACT.href)} className="btn-primary mt-3">{CTA.CONTACT.label} →</button>
                    </div>
                </div>
            )}
        </header>
    );
}
